import { useEffect } from "react";
import { useTimeout } from "primereact/hooks";
import { useNavigate } from "react-router";

import { useMessenger } from "@/packages/portal-messenger";
import { PathNamekRoutes } from "@/router";

import Messages from "../constants/PortalMessenger/messages";
import { TokensT, getLegacyTokens, hasLegacyTokens, saveLegacyTokens } from "../store/tokens.store";
import { postOrionAuthenticate } from "../services/orionv2";
import { isInIframe } from "../helpers/isInIframe";

import { useSessionMan } from "./useSessionMan.hook";

export const useAuthentication = () => {
  const navigate = useNavigate();
  const { startSession, clearSession, isSignedIn } = useSessionMan();
  const [clearTimeout] = useTimeout(() => {
    handleTimeout();
  }, 60000);

  const { isConnectionEstablished, subscribeToDataReceived, sendDataMessage } = useMessenger();

  const fetchAuthentication = async (tokenLegacy: string) => {
    return postOrionAuthenticate(tokenLegacy);
  };

  const handleTimeout = () => {
    if (isInIframe()) {
      navigate(`/${PathNamekRoutes.UNAUTHORIZED}`, {
        replace: true,
      });
      return;
    }

    navigate(`/${PathNamekRoutes.LOGIN}`, {
      replace: true,
    });
  };

  useEffect(() => {
    if (isInIframe()) {
      return;
    }

    if (!hasLegacyTokens()) {
      handleTimeout();
    }

    const doLogin = async () => {
      try {
        const data = await fetchAuthentication(getLegacyTokens().authToken);
        startSession(data);
        clearTimeout();
      } catch (e) {
        handleTimeout();
      }
    };

    doLogin();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isConnectionEstablished) {
      return;
    }

    const { key, value } = Messages.requestAuthTokens;

    subscribeToDataReceived(async (dataKey, dataValue) => {
      if (dataKey === value) {
        const dataValueJson = JSON.parse(dataValue);
        const legacyTokens: TokensT = dataValueJson;

        clearTimeout();
        clearSession();
        saveLegacyTokens(legacyTokens);
        const data = await fetchAuthentication(legacyTokens.authToken);
        startSession(data);
      }
    });

    const timeoutId = setTimeout(() => {
      sendDataMessage(key, value);
    });

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnectionEstablished]);

  return {
    isConnectionEstablished,
    isSignedIn,
  };
};
